import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TableCell, TableRow } from '@mui/material';
import useUserDetails from '../hooks/useUserDetails';
import useErrorModal from '../hooks/useErrorModal';
import CustomButton from '../reusable/CustomButton';
import CustomTable, { TableHeaderData } from '../reusable/CustomTable';
import CustomPagination from '../reusable/CustomPagination';
import Loader from '../reusable/Loader';
import Panel from '../reusable/Panel';
import Tag from '../reusable/Tag';
import { getUserActivityLog } from '../../services/userManagementService';
import { UserActivityLogItem } from '../../interfaces/UserManagementInterface';
import { ACTIVITY_LOG_ITEMS_PER_PAGE } from '../../constants';
import { formatDateTime } from '../../utils';
import { getErrorDetail } from './userManagementUtils';
import { formatActivityDetails, getActionLabel, getActionTagType } from './activityLogUtils';
import GoBackIcon from '../assets/transcript-selection-arrow-left.svg';
import './UserManagementLayout.css';

const tableHeaders: TableHeaderData<UserActivityLogItem>[] = [
  { key: 'created_on', label: 'Date' },
  { key: 'actor_name', label: 'Performed by' },
  { key: 'action', label: 'Action' },
  { key: 'target_name', label: 'User' },
  { key: 'details', label: 'Details' },
];

const UserActivityLogPage = () => {
  const {
    userDetails: { data: userDetails },
  } = useUserDetails();
  const { handleOpen: handleOpenErrorModal } = useErrorModal();
  const navigate = useNavigate();

  const [logs, setLogs] = useState<UserActivityLogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  useEffect(() => {
    if (!userDetails) return;

    let active = true;
    const fetchActivityLog = async () => {
      try {
        setLoading(true);
        const response = await getUserActivityLog(userDetails.access_token, {
          page: currentPage,
          size: ACTIVITY_LOG_ITEMS_PER_PAGE,
        });
        if (!active) return;
        setLogs(response.data.items);
        setTotalItems(response.data.total);
        setTotalPages(response.data.pages);
      } catch (error: unknown) {
        if (!active) return;
        console.error('Error fetching activity log:', error);
        handleOpenErrorModal(getErrorDetail(error, 'Failed to fetch activity log. Please try again.'), error as Error);
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchActivityLog();
    return () => {
      active = false;
    };
  }, [userDetails, currentPage, handleOpenErrorModal]);

  if (!userDetails) return <Loader />;

  return (
    <div className="main-content user-management-page">
      <CustomButton
        className="user-management-back-button"
        variant="text"
        buttonText="Back"
        icon={<img src={GoBackIcon} alt="go back" />}
        iconPosition="start"
        buttonAction={() => navigate(-1)}
      />
      <div className="user-management-header">
        <h1 className="user-management-page-title">Activity Log</h1>
        <p className="user-management-description">History of user management activity</p>
      </div>
      {loading ? (
        <Loader />
      ) : logs.length === 0 ? (
        <Panel className="user-management-panel user-management-table-panel">
          <p className="user-management-empty-state">No activity yet.</p>
        </Panel>
      ) : (
        <>
          <Panel className="user-management-panel user-management-table-panel">
            <CustomTable tableHeaderData={tableHeaders}>
              {logs.map((log) => (
                <TableRow key={log.id}>
                  <TableCell>{formatDateTime(log.created_on)}</TableCell>
                  <TableCell>{log.actor_name}</TableCell>
                  <TableCell>
                    <Tag
                      type={getActionTagType(log.action, log.details)}
                      text={getActionLabel(log.action, log.details)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <div>{log.target_name ?? '—'}</div>
                    {log.target_email ? <div className="user-management-email-cell">{log.target_email}</div> : null}
                  </TableCell>
                  <TableCell>
                    {formatActivityDetails(log.action, log.details).map((line) => (
                      <div key={line}>{line}</div>
                    ))}
                  </TableCell>
                </TableRow>
              ))}
            </CustomTable>
          </Panel>
          {totalPages > 1 && (
            <CustomPagination
              currentPage={currentPage}
              pages={totalPages}
              totalItems={totalItems}
              pageSize={ACTIVITY_LOG_ITEMS_PER_PAGE}
              handlePageChange={setCurrentPage}
            />
          )}
        </>
      )}
    </div>
  );
};

export default UserActivityLogPage;
